import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import Chart from 'chart.js/auto';
import React from 'react';
import useRect from 'hooks/common/useRect';

const time = (index) => `${Math.floor(index / 4).toString().padStart(2, '0')}${((index % 4) * 15).toString().padStart(2, '0')}`;

const labels = Array.from({ length: 24 * 4 + 1 }).map((_, index) => {
  return time(index);
});

const config = (hash, onComplete, duration) => ({
  type: 'scatter',
  data: {
    labels,
    datasets: [
      {
        label: T('고혈당'),
        data: labels.map((_, index) => (hash.H || {})[time(index)]),
        backgroundColor: '#FF995A',
      }, {
        label: T('저혈당'),
        data: labels.map((_, index) => (hash.L || {})[time(index)]),
        backgroundColor: '#5100FF',
      }, {
        label: T('변동혈당'),
        data: labels.map((_, index) => (hash.V || {})[time(index)]),
        backgroundColor: '#13C1C7',
      }, {
        label: T('일반'),
        data: labels.map((_, index) => (hash.N || {})[time(index)]),
        backgroundColor: '#AAAAAA',
      }
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        top: 30,
        left: 50,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 0:
                return false;
              default:
                return true;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        border: {
          dash: [4, 4],
        },
        min: 0,
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 1,
          display: true,
        },
        ticks: {
          callback: (value) => {
            switch (value) {
              case 0: return '0H';
              case 600: return '6H';
              case 1200: return '12H';
              case 1800: return '18H';
              case 2400: return '24H';
              default: return '';
            }
          },
          stepSize: 600,
          color: '#AAAAAA',
        },
      },
      y: {
        min: 0,
        max: hash.max,
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: 0.5,
          color: '#AAAAAA',
        },
        ticks: {
          font: '11px Pretendard',
          color: '#AAAAAA',
          stepSize: 5,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = (context[0].label || '').split('');
            label.splice(2, 0, ':');
            return label.join('');
          },
          label: (context) => {
            const title = context.dataset.label
            return T('{0} ({1})', title, context.parsed.y);
          },
        },
      },
    },
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left + 45;
      const right = xAxis.right;

      ctx.save();

      ctx.font = '12px Pretendard';
      ctx.fillStyle = '#aaaaaa';
      const y = yAxis.getPixelForValue(60);

      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(T('볼루스(unit)'), left - 3, y - 15);

      ctx.restore();
    }
  }]
});

export default function AbnormalInsulinBoxChart(props) {
  const {
    abnormal_bld_sugr_graph_list = [],
    onComplete,
    duration,
    ...rest
  } = props;

  const hash = React.useMemo(() => {
    const data = {}
    let max = 10;
    abnormal_bld_sugr_graph_list.forEach((item) => {
      const {
        bolus_inject_time,
        bld_sugr_tp,
        bolus_inject_amt,
      } = item;
      if (max < bolus_inject_amt) max = bolus_inject_amt;
      data[bld_sugr_tp] = {
        ...(data[bld_sugr_tp] || {}),
        [bolus_inject_time]: +bolus_inject_amt,
      };
    }, {});
    data.max = Math.ceil(max / 10) * 10;
    return data;
  }, [abnormal_bld_sugr_graph_list]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(hash, onComplete, duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [duration, hash, onComplete]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div
      {...ex(['Chart', 'AbnormalInsulinBoxChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}
